// Single manifest driving the sidebar and the router. The nav is two-level:
// top-level entries are either a plain link (Dashboard) or a disclosure
// section with child modules. Adding a future child (e.g. a "Knowledge Base"
// split out of Skills, or more course tools) is a one-line addition to the
// relevant section's `children`.

import type { ComponentType } from "react";
import {
  IconBooks,
  IconBulb,
  IconDeviceMobile,
  IconLayoutDashboard,
  IconPhone,
  IconPlugConnected,
  IconRoute,
  IconSchool,
  IconUsers,
  IconWaveSine,
} from "@tabler/icons-react";
import { COURSES_ENABLED } from "./config/env";

type ModuleIcon = ComponentType<{ size?: number | string; stroke?: number | string }>;

export interface StudioModule {
  key: string;
  label: string;
  icon: ModuleIcon;
  path: string;
  comingSoon?: boolean;
  description?: string; // shown on the coming-soon placeholder
}

export type NavEntry =
  | { kind: "link"; module: StudioModule }
  | { kind: "section"; key: string; label: string; children: StudioModule[] };

export const NAV: NavEntry[] = [
  {
    kind: "link",
    module: { key: "dashboard", label: "Dashboard", icon: IconLayoutDashboard, path: "/dashboard" },
  },
  {
    kind: "section",
    key: "companion",
    label: "Companion",
    children: [
      // Label is "Guides" (not "Companions") to avoid "Companion > Companions";
      // the route stays /companions and the /guides editor maps here too.
      { key: "guides", label: "Guides", icon: IconUsers, path: "/companions" },
      { key: "skills", label: "Skills", icon: IconBooks, path: "/skills" },
      // Clinically reviewed conversation playbooks, served to every guide.
      { key: "talk-tracks", label: "Talk tracks", icon: IconRoute, path: "/talk-tracks" },
      // Which voice engine each call uses: rollout policy, engine profiles, test users.
      { key: "voice-engines", label: "Voice engines", icon: IconWaveSine, path: "/voice-engines" },
      // Future: { key: "knowledge-base", label: "Knowledge Base", icon: …, path: "/knowledge-base" }
      // once knowledge bases split out of Skills.
    ],
  },
  {
    kind: "section",
    key: "courses",
    label: "Courses",
    children: [
      {
        key: "courses",
        label: "Courses",
        icon: IconSchool,
        path: "/courses",
        // A real section once the aura-courses web config is present
        // (VITE_COURSES_FIREBASE_*); otherwise the coming-soon placeholder.
        comingSoon: !COURSES_ENABLED,
        description: "Author and publish courses without leaving the Studio.",
      },
    ],
  },
  {
    kind: "section",
    key: "observe",
    label: "Observe",
    children: [
      { key: "calls", label: "Calls", icon: IconPhone, path: "/calls" },
      {
        key: "insights",
        label: "Evals",
        icon: IconBulb,
        path: "/insights",
        description: "Rubric quality scores on sampled production calls, per companion version.",
      },
      // Weekly active users by app build, and (CV-S2) the force-update floors.
      { key: "app-versions", label: "App versions", icon: IconDeviceMobile, path: "/app-versions" },
    ],
  },
  {
    kind: "section",
    key: "access",
    label: "Access",
    children: [
      // Your MCP connections (Claude Code, claude.ai, ChatGPT) and Revoke.
      { key: "connected-agents", label: "Connected agents", icon: IconPlugConnected, path: "/connected-agents" },
    ],
  },
];

// Flat module list (drives the coming-soon routes in the router).
export const MODULES: StudioModule[] = NAV.flatMap((e) =>
  e.kind === "link" ? [e.module] : e.children,
);

// Editor routes that belong to a module but live outside its path prefix.
const EXTRA_PREFIXES: Record<string, string[]> = {
  guides: ["/guides"],
};

export function isModuleActive(module: StudioModule, pathname: string): boolean {
  if (pathname === module.path || pathname.startsWith(`${module.path}/`)) return true;
  return (EXTRA_PREFIXES[module.key] || []).some((p) => pathname.startsWith(p));
}
